import type { JSX } from 'react';

import { Bell } from 'lucide-react';

import { Alert, AlertDescription } from '../../../components/Alert/Alert';
import { Button } from '../../../components/Button/Button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../../components/Card/Card';
import { CustomSwitch } from '../../../components/CustomSwitch/CustomSwitch';
import {
  isAppInstalled,
  isMobileViewport,
  openInstallHint,
} from '../../InstallPrompt/useInstallPrompt';

import styles from './NotificationSettings.module.css';
import { usePushNotifications } from './usePushNotifications';

export default function NotificationSettings(): JSX.Element {
  const {
    isSupported,
    isConfiguredOnServer,
    permission,
    isSubscribed,
    busy,
    error,
    subscribe,
    unsubscribe,
  } = usePushNotifications();

  const handleToggle = (checked: boolean): void => {
    if (checked) {
      void subscribe();
    } else {
      void unsubscribe();
    }
  };

  const canToggle = isSupported && isConfiguredOnServer && permission !== 'denied';
  // On mobile, offer the install shortcut — iOS only delivers push to an
  // installed (home-screen) PWA.
  const showInstallHint = isMobileViewport() && !isAppInstalled();

  return (
    <Card className={styles.Card} noHover>
      <CardHeader>
        <div className={styles.Header}>
          <span className={styles.HeaderIcon} aria-hidden="true">
            <Bell />
          </span>
          <div className={styles.HeaderText}>
            <CardTitle>Push notifications</CardTitle>
            <CardDescription>
              Get notified on this device when subscriptions renew, customers churn, or invoices are
              paid or overdue. Install Revvly to your home screen to receive them when the app is
              closed.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className={styles.Content}>
        {!isSupported && (
          <Alert variant="info">
            <AlertDescription>This browser does not support push notifications.</AlertDescription>
          </Alert>
        )}

        {isSupported && !isConfiguredOnServer && (
          <Alert variant="info">
            <AlertDescription>
              Push notifications are not enabled on the server yet. Contact your administrator.
            </AlertDescription>
          </Alert>
        )}

        {isSupported && permission === 'denied' && (
          <Alert variant="warning">
            <AlertDescription>
              Notifications are blocked in your browser settings. Enable them for this site, then
              try again.
            </AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {canToggle && (
          <div className={styles.Toggle}>
            <CustomSwitch
              checked={isSubscribed}
              disabled={busy}
              onChange={handleToggle}
              label="Enable push notifications on this device"
            />
          </div>
        )}

        {showInstallHint && (
          <div className={styles.InstallRow}>
            <Button variant="outline" size="sm" onClick={openInstallHint}>
              Add Revvly to your home screen
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
